import * as React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from '@/toast/toastStore'
import apiService from '@/services/api'
import { oauthService } from '@/services/oauthService'
import { useOAuthProviders } from '@/hooks/useOAuthProviders'
import { Button } from '@/components/ui/Button'
import { AuthShell } from '@/components/auth/AuthShell'
import { FormField, OrDivider } from '@/components/auth/FormField'
import { SocialLoginButtonsV2 } from '@/components/auth/SocialLoginButtonsV2'
import type { OAuthProvider } from '@/types'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface FieldErrors {
  name?: string
  email?: string
  password?: string
  confirmPassword?: string
  agreement?: string
}

/**
 * Sottovo 新規登録ページ (`/auth/register`)。
 *
 * 既存 RegisterPage の挙動をそのまま踏襲する:
 *   - apiService.register({ name, email, password }) を直接呼ぶ (useAuth().register ではない)
 *   - 成功時 → navigate('/auth/login') (自動ログインはしない)
 *   - oauthService.startOAuthFlow(provider) で OAuth フロー開始
 *
 * バリデーションも既存ルールを踏襲: 名前必須 / email 形式 / password 8 文字以上 /
 * confirm 一致 / 利用規約同意。UI のみ v2 ブランドで再構築。
 */
export default function RegisterPage() {
  const navigate = useNavigate()
  const { providers: oauthProviders, isLoading: oauthProvidersLoading } = useOAuthProviders()

  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')
  const [agreement, setAgreement] = React.useState(false)
  const [errors, setErrors] = React.useState<FieldErrors>({})
  const [loading, setLoading] = React.useState(false)
  const [oauthLoading, setOAuthLoading] = React.useState(false)
  const [oauthProvider, setOAuthProvider] = React.useState<OAuthProvider | null>(null)

  function validate(): FieldErrors {
    const next: FieldErrors = {}
    if (!name) next.name = 'お名前を入力してください'
    if (!email) next.email = 'メールアドレスを入力してください'
    else if (!EMAIL_RE.test(email))
      next.email = '有効なメールアドレスを入力してください'
    if (!password) next.password = 'パスワードを入力してください'
    else if (password.length < 8)
      next.password = 'パスワードは8文字以上で入力してください'
    if (!confirmPassword) next.confirmPassword = 'パスワード確認を入力してください'
    else if (confirmPassword !== password)
      next.confirmPassword = 'パスワードが一致しません'
    if (!agreement) next.agreement = '利用規約に同意してください'
    return next
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const validation = validate()
    setErrors(validation)
    if (Object.keys(validation).length > 0) return

    setLoading(true)
    try {
      const response = await apiService.register({ name, email, password })
      if (response.success) {
        toast.success('アカウントが作成されました。メールを確認してください。')
        navigate('/auth/login')
      } else {
        toast.error(response.error || 'アカウント作成に失敗しました。')
      }
    } catch (error) {
      console.error('Register error:', error)
      toast.error('アカウント作成に失敗しました。')
    } finally {
      setLoading(false)
    }
  }

  function handleSocialLogin(provider: OAuthProvider) {
    setOAuthLoading(true)
    setOAuthProvider(provider)
    try {
      oauthService.startOAuthFlow(provider)
    } catch (error) {
      console.error('OAuth start error:', error)
      setOAuthLoading(false)
      setOAuthProvider(null)
    }
  }

  return (
    <AuthShell
      title="はじめまして"
      subtitle="数十秒で、あなたのアシスタントを迎えられます"
    >
      <SocialLoginButtonsV2
        onLogin={handleSocialLogin}
        loading={oauthLoading}
        loadingProvider={oauthProvider}
        disabled={loading}
        providers={oauthProviders}
        providersLoading={oauthProvidersLoading}
      />

      {(oauthProvidersLoading || oauthProviders.length > 0) && (
        <>
          <p className="mt-2 text-center text-xs leading-relaxed text-v2-ink-soft">
            ソーシャルアカウントで登録すると、
            <Link to="/terms" target="_blank" className="text-v2-primary underline-offset-4 hover:underline">
              利用規約
            </Link>
            と
            <Link to="/privacy" target="_blank" className="text-v2-primary underline-offset-4 hover:underline">
              プライバシーポリシー
            </Link>
            に同意したものとみなされます
          </p>

          <div className="my-6">
            <OrDivider label="または メールアドレスで登録" />
          </div>
        </>
      )}

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
        <FormField
          id="register-name"
          label="お名前"
          type="text"
          autoComplete="name"
          placeholder="山田 太郎"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={errors.name}
        />

        <FormField
          id="register-email"
          label="メールアドレス"
          type="email"
          autoComplete="email"
          placeholder="your.email@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
        />

        <FormField
          id="register-password"
          label="パスワード"
          type="password"
          autoComplete="new-password"
          placeholder="パスワードを入力"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
        />

        <FormField
          id="register-confirm-password"
          label="パスワード確認"
          type="password"
          autoComplete="new-password"
          placeholder="パスワードを再入力"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={errors.confirmPassword}
        />

        <div className="flex flex-col gap-1.5">
          <label className="flex items-start gap-2.5 text-sm text-v2-ink-muted">
            <input
              type="checkbox"
              checked={agreement}
              onChange={(e) => setAgreement(e.target.checked)}
              aria-invalid={errors.agreement ? true : undefined}
              aria-describedby={errors.agreement ? 'register-agreement-error' : undefined}
              className="mt-0.5 h-4 w-4 rounded border-v2-border text-v2-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-primary focus-visible:ring-offset-2"
            />
            <span>
              <Link to="/terms" target="_blank" className="text-v2-primary underline-offset-4 hover:underline">
                利用規約
              </Link>
              および
              <Link to="/privacy" target="_blank" className="text-v2-primary underline-offset-4 hover:underline">
                プライバシーポリシー
              </Link>
              に同意する
            </span>
          </label>
          {errors.agreement ? (
            <p id="register-agreement-error" role="alert" className="text-sm text-v2-danger">
              {errors.agreement}
            </p>
          ) : null}
        </div>

        <Button
          type="submit"
          size="md"
          className="w-full"
          disabled={loading || oauthLoading}
        >
          {loading ? '作成中…' : 'アカウントを作成'}
        </Button>
      </form>

      <p className="mt-8 text-center text-sm text-v2-ink-muted">
        既にアカウントをお持ちの方は{' '}
        <Link
          to="/auth/login"
          className="font-medium text-v2-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-primary focus-visible:ring-offset-2"
        >
          ログイン
        </Link>
      </p>
    </AuthShell>
  )
}
